import type {
  CardStack,
  CardStackAttributes,
  LessonCard,
  ParentCardStack,
} from "@shared/models/card-stack"
import type { CardAttrsBase } from "@shared/unified-tiptap/util/card"
import { objectId } from "@shared/util/object-id"
import { extractFromProseMirrorDoc } from "./extracted"
import { initialIdList, parseCardStackProseMirrorJson } from "./prose-mirror-utils"
import {
  legacyCardsFromCardStackDoc,
  readonlyLegacyCopy,
  readonlyUnifiedCopy,
} from "./unified-migration"

/**
 * Returns a deep copy of originalAttrs, with additional updates:
 * - Sets the schemaVersion according to cloneAsUnified, migrating if needed.
 * - Assigns new IDs to cards and characters. (Note: this will break external data like comments
 * that reference character IDs; okay because these aren't copied alongside.)
 * - Resets fields like commentIds & cachedDates that shouldn't be copied between lessons or from templates to lessons.
 * - Sets the parentCardStack.
 *
 * Note: usedAs is an exact copy of the input - you need to change this if you're
 * cloning a template into a non-template.
 *
 * @param cloneAsUnified Whether the cloned attributes should be for a UnifiedCardStack.
 * Usually you'll set this to `planbook.attributes.flags.unifiedLessons`,
 * except for routines and routine-templates.
 */
export function cloneCardStackAttributesWithNewIds(
  originalAttrs: CardStackAttributes,
  parentCardStack: ParentCardStack | null | undefined,
  cloneAsUnified: boolean,
  options: {
    overrideTitle?: string
    mapCardId?: (parentCardId: string) => string
    onMigrationError?: (error: unknown, card: LessonCard) => void
  } = {},
): CardStackAttributes {
  if (cloneAsUnified) {
    // We'll output UnifiedCardStackAttributes.
    const newAttrs = readonlyUnifiedCopy(
      { attributes: originalAttrs } as CardStack,
      options.onMigrationError,
    ).attributes

    // Override title.
    if (options.overrideTitle !== undefined) {
      newAttrs.title = options.overrideTitle
    }

    // Assign new IDs to cards and characters.
    if (newAttrs.proseMirror) {
      const originalDoc = parseCardStackProseMirrorJson(newAttrs.proseMirror.json)
      const newContent = [
        // Give each card a fresh id, stable between applications.
        // Also reset card commentIds (not copied).
        ...originalDoc.content.content.map((docChild) => {
          if (docChild.type.isInGroup("card")) {
            const attrs = docChild.attrs as CardAttrsBase
            const newId = options.mapCardId ? options.mapCardId(attrs.id) : objectId()
            return docChild.type.create(
              {
                ...attrs,
                id: newId,
                parentCardId: attrs.id,
                commentIds: [],
              } satisfies CardAttrsBase,
              docChild.content,
              docChild.marks,
            )
          } else {
            return docChild
          }
        }),
      ]
      const newDoc = originalDoc.type.create(null, newContent)

      newAttrs.proseMirror = { json: newDoc.toJSON(), ids: initialIdList(newDoc).save() }
      newAttrs.extracted = extractFromProseMirrorDoc(newDoc)
      newAttrs.cards = legacyCardsFromCardStackDoc(newDoc)
    }

    // Reset non-copied fields and set the parentCardStack.
    newAttrs.commentIds = []
    delete newAttrs.cachedDates
    newAttrs.parentCardStack = parentCardStack

    return newAttrs
  } else {
    // We'll output LegacyCardStackAttributes.
    const newAttrs = readonlyLegacyCopy({ attributes: originalAttrs } as CardStack).attributes

    // Override title.
    if (options.overrideTitle !== undefined) {
      newAttrs.title = options.overrideTitle
    }

    // Assign new IDs to cards.
    if (newAttrs.cards) {
      for (const card of newAttrs.cards) {
        const newId = options.mapCardId ? options.mapCardId(card.id) : objectId()
        card.attributes.parentCardId = card.id
        card.id = newId
      }
    } else {
      newAttrs.cards = null
    }

    // Reset non-copied fields and set the parentCardStack.
    newAttrs.commentIds = []
    delete newAttrs.cachedDates
    newAttrs.parentCardStack = parentCardStack

    return newAttrs
  }
}
